import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLingui } from "@lingui/react";
import {
  ArrowUpDown,
  Columns2,
  Columns3,
  LayoutGrid,
  LayoutList,
  Loader2,
  Search,
  SlidersHorizontal,
} from "lucide-react";

export type FilterToolbarViewMode = "list" | "tile" | "panel";

export interface FilterToolbarStatusOption {
  value: string;
  label: string;
}

interface FilterToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  viewMode: FilterToolbarViewMode;
  onViewModeChange: (mode: FilterToolbarViewMode) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  statusOptions: FilterToolbarStatusOption[];
  showColumns?: boolean;
  showPanel?: boolean;
  isRefreshing?: boolean;
  searchPlaceholder?: string;
}

export function FilterToolbar({
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
  statusFilter,
  onStatusFilterChange,
  statusOptions,
  showColumns = false,
  showPanel = true,
  isRefreshing = false,
  searchPlaceholder,
}: FilterToolbarProps) {
  const { i18n } = useLingui();

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-(--color-text-secondary)" />
        <Input
          placeholder={searchPlaceholder ?? i18n._("Search…")}
          className="pl-8 h-9 text-sm"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="ml-auto flex items-center gap-2">
        <div
          className={`flex h-9 items-center gap-1.5 text-xs text-(--color-text-secondary) transition-opacity ${
            isRefreshing ? "opacity-100" : "opacity-0"
          }`}
          aria-live="polite"
          aria-hidden={!isRefreshing}
        >
          <Loader2 className="size-3.5 animate-spin" />
          <span>{i18n._("Updating")}</span>
        </div>

        {statusOptions.length > 0 && (
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder={i18n._("Status")} />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {showColumns && (
          <Button variant="outline" size="sm" className="h-9 gap-1.5">
            <Columns2 className="size-4" />
            {i18n._("Columns")}
          </Button>
        )}

        <Button variant="outline" size="sm" className="h-9 gap-1.5">
          <ArrowUpDown className="size-4" />
          {i18n._("Sort")}
        </Button>

        <Button variant="outline" size="sm" className="h-9 gap-1.5">
          <SlidersHorizontal className="size-4" />
          {i18n._("Filter")}
        </Button>

        <div
          className="flex items-center rounded-md border"
          role="group"
          aria-label={i18n._("View mode")}
        >
          <button
            type="button"
            onClick={() => onViewModeChange("list")}
            aria-label={i18n._("List view")}
            aria-pressed={viewMode === "list"}
            className={`p-2 ${viewMode === "list" ? "bg-(--color-surface-muted)" : ""}`}
          >
            <LayoutList className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange("tile")}
            aria-label={i18n._("Tile view")}
            aria-pressed={viewMode === "tile"}
            className={`p-2 ${viewMode === "tile" ? "bg-(--color-surface-muted)" : ""}`}
          >
            <LayoutGrid className="size-4" />
          </button>
          {showPanel && (
            <button
              type="button"
              onClick={() => onViewModeChange("panel")}
              aria-label={i18n._("Panel view")}
              aria-pressed={viewMode === "panel"}
              className={`p-2 ${viewMode === "panel" ? "bg-(--color-surface-muted)" : ""}`}
            >
              <Columns3 className="size-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
